import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import ReactTable from 'react-table';
import {
  Col,
  FormControl,
  Row,
} from 'react-bootstrap';
import { DragDropContext } from 'react-beautiful-dnd';
import {
  DragTrComponent,
  DropTbodyComponent,
} from './ScheduleDnDTable';
import Filters from '../../components/Filters/Filters';
import { displayDate } from '../../utils/date';
import { withTranslation } from 'react-i18next';
import { showConfirmModal } from '../../modules/modals/confirmService';
import ReasonCodeSelection from '../../components/ResonCodeSelection/ReasonCodeSelection';
import { BatchTicketStatus } from '../../constants/BatchTicketStatus';
import { toJS } from 'mobx';
import { OrderOrigin } from '../../constants/OrderOrigin';

@inject('scheduleStore', 'sidebarStore', 'batchStore', 'materialStore', 'connectionStore', 'ticketStore', 'notifierStore', 'newAuthStore')
@observer
class ScheduleTable extends Component {
  constructor(props) {
    super(props);

    this.state = {
      filter: '',
      reason: '',
    };

  }

  onDelete = (order) => {
    const { scheduleStore, connectionStore, materialStore, batchStore, newAuthStore, t } = this.props;
    const { id, origin } = order;

    showConfirmModal({
      title: t('Are you sure you want to delete order?', { id }),
      okLabel: t('Yes'),
      cancelLabel: t('No'),
      enableEscape: false,
      message: origin !== OrderOrigin.Dispatch ? '' : (
        <ReasonCodeSelection reasonCode={materialStore.reasonCode} onChange={reason => {
          this.setState({ reason });
        }}/>
      ),
    })
      .then(() => {
        const { isOnline } = connectionStore;

        if (origin === OrderOrigin.Local) {

          if (!isOnline || scheduleStore.offlineOrders.find(v => v.id === id)) {
            scheduleStore.offlineOrders = scheduleStore.offlineOrders.filter(v => v.id !== id);
            newAuthStore.storeDB.init().then(() => newAuthStore.storeDB.save(scheduleStore.offlineOrdersNameSpace, toJS(scheduleStore.offlineOrders)));
            scheduleStore.localOrders = scheduleStore.localOrders.filter(v => v.id !== id);
            return;
          }

          scheduleStore.deleteLocalOrder(id)
            .catch(e => {
              batchStore.showError(e.message);
            });

        } else if (origin === OrderOrigin.External) {

          if (!isOnline) {
            scheduleStore.schedules = scheduleStore.schedules.filter(v => v.id !== id);
          }

          scheduleStore.deleteExternalOrder(id)
            .catch(e => {
              batchStore.showError(e.message);
            });
        } else {

          if (!isOnline) {
            scheduleStore.schedules = scheduleStore.schedules.filter(v => v.id !== id);
          }

          scheduleStore.deleteDispatchOrder(id, this.state.reason)
            .catch(e => {
              batchStore.showError(e.message);
            });
        }
      });

  };

  batchOrder = order => {
    const { batchStore, sidebarStore } = this.props;
    batchStore.setOrderId(null);
    sidebarStore.onToggle('batchForm');

    setTimeout(() => {
      batchStore.setOrderId(order.id);
      this.displayNotes(order.notes);
    }, 0);

  };

  displayNotes(notes = []) {
    const { notifierStore, t } = this.props;

    if (notes.length) {
      notes.forEach(note => {
        if (note.autoDisplay) {
          showConfirmModal({
            title: t('Order note'),
            message: note.description,
            okLabel: t('OK'),
            cancelLabel: '',
            enableEscape: false,
          });
        } else {
          notifierStore.showInfo(note.description, 5000);
        }
      });
    }
  }

  printOrder = data => {
    const { ticketStore, batchStore, materialStore } = this.props;
    const mix = data.products && data.products.length ? data.products.find(v => v.isMix) : null;
    const payload = { ...data, quantityToBeBatched: mix ? mix.quantity : data.orderMixQuantity };
    let promise;

    if (data.origin === OrderOrigin.Local) {
      promise = ticketStore.createLocalOrderOnBatchStart(payload);
    } else {
      promise = ticketStore.createTicketOnBatchStart(payload);
    }

    promise
      .then(data => {
        return ticketStore.startBatchTicket(data.id);
      })
      .then(data => {
        return ticketStore.updateTicketOnBatchEnd(data);
      })
      .then(data => {
        batchStore.showInfo('Ticket ready to be printed!');
        if (batchStore.canReduceInventory) {
          materialStore.updateMaterialInventoryUseForNonBatchProducts(data);
        }
      })
      .catch(e => { batchStore.showError({ key: 'Error', props: { error: e.message }}); });
  };

  getMixQty(data) {
    const mix = data && data.products ? data.products.find(v => v.isMix) : null;
    return mix && mix.quantity ? mix.quantity : 0;
  }

  getColumns = () => {
    const { sidebarStore, scheduleStore, batchStore, newAuthStore, t, displayLocalOrders = false } = this.props;
    const { previewOrder, editOrder, timezone } = scheduleStore;
    const { canEmit } = batchStore;
    const { user } = newAuthStore;

    return [
      {
        Header: t('Time'),
        id: 'date',
        width: 100,
        sortable: false,
        accessor: d => (
          <div title={displayDate(d.date, timezone, 'L LT', user.language)}>
            <span className={`schedule-color-level schedule-color-level--${d.colorLevel}`}/>
            {displayDate(d.date, timezone, 'LT', user.language)}
          </div>
        ),
      },
      {
        Header: displayLocalOrders ? '' : t('Ticket'),
        accessor: d => d.dispatchTicketCode,
        id: 'code',
        className: 'sticky',
        headerClassName: 'sticky',
        width: 80,
        sortable: false,
      },
      {
        Header: t('Order'),
        accessor: d => d.dispatchOrderCode || d.code,
        id: 'dispatchOrderCode',
        width: 80,
        sortable: false,
      },
      {
        Header: t('Qty'),
        id: 'orderMixQuantity',
        width: 60,
        sortable: false,
        accessor: d => this.getMixQty(d),
      },
      {
        Header: t('Balance'),
        id: 'orderDeliveredMixQuantity',
        width: 70,
        sortable: false,
        accessor: d => (d.orderMixQuantity - (d.orderDeliveredMixQuantity || 0)),
      },
      {
        Header: t('Truck'),
        accessor: 'truckCode',
        id: 'truckCode',
        className: 'sticky',
        headerClassName: 'sticky',
        width: 70,
        sortable: false,
      },
      {
        Header: t('Customer'),
        accessor: 'customerName',
        id: 'customerName',
        width: 100,
        sortable: false,
      },
      {
        Header: t('Ship To'),
        accessor: 'shipToAddress',
        id: 'shipToAddress',
        width: 150,
        sortable: false,
      },
      {
        Header: t('Action'),
        sortable: false,
        Cell: row => !(canEmit || batchStore.canSendRemoteInstruction()) ? '' : (
          <div className="actions text-right">
            {row.original.batchTicketStatus !== BatchTicketStatus.Batched && !scheduleStore.isEmptyMixDesign(row.original) && (
              <a className="btn btn-sm btn-default btn-action"
                title={t('Batch')}
                onClick={() => {
                  this.batchOrder(row.original);
                }}
              >
                <i className="fa fa-play"/>
              </a>
            )}
            {!row.original.isBatched && scheduleStore.isEmptyMixDesign(row.original) && (
              <a className="btn btn-sm btn-default btn-action"
                title={t('Print')}
                onClick={() => {
                  this.printOrder(row.original);
                  sidebarStore.onToggle();
                }}
              >
                <i className="fa fa-stop"/>
              </a>
            )}
            {(row.original.origin !== OrderOrigin.Local || row.original.batchTicketStatus === BatchTicketStatus.Batched) && (
              <a className="btn btn-sm btn-default btn-action"
                title={t('Preview')}
                onClick={() => {
                  previewOrder(row.original.id);
                  sidebarStore.onToggle('orderForm');
                }}>
                <i className="fa fa-eye"/>
              </a>
            )}
            {row.original.origin === OrderOrigin.Local && row.original.batchTicketStatus !== BatchTicketStatus.Batched && (
              <a className="btn btn-sm btn-default btn-action"
                title={t('Edit')}
                onClick={() => {
                  editOrder(row.original.id);
                  sidebarStore.onToggle('orderForm');
                }}>
                <i className="fa fa-pencil"/>
              </a>
            )}
            {row.original.batchTicketStatus !== BatchTicketStatus.Batched && (
              <a className="btn btn-sm btn-default btn-action"
                title={t('Delete')}
                onClick={() => this.onDelete(row.original)}>
                <i className="fa fa-remove"/>
              </a>
            )}
          </div>
        ),
      },
    ];
  };

  reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  handleDragEnd = result => {
    const { scheduleStore, displayLocalOrders = false } = this.props;
    let data;
    if (displayLocalOrders) {
      data = [...scheduleStore.localOrders];
    } else {
      data = [...scheduleStore.schedules];
    }

    if (!result.destination) {
      return;
    }

    const orderedData = this.reorder(
      data,
      result.source.index,
      result.destination.index
    );

    if (displayLocalOrders) {
      this.props.scheduleStore.localOrders = orderedData;
    } else {
      this.props.scheduleStore.schedules = orderedData;
    }

  };

  getTrProps = (state, rowInfo) => {
    return { rowInfo };
  };

  handleChange = (e) => {
    const filter = e.target.value;
    this.setState({ filter });
  };

  get data() {
    const { displayLocalOrders = false, scheduleStore } = this.props;

    let db = [];
    if (displayLocalOrders) {
      db = db.concat(scheduleStore.localOrders);
    } else {
      db = db.concat(scheduleStore.schedules);
    }

    let dataToFilter = [];
    db.forEach(item => {
      const mix = item.products ? item.products.find(p => p.isMix) || {} : {};
      dataToFilter.push({
        ...item,
        _accountTypeName: item.accountType || '',
        _mixCode: mix.itemCode,
        _mixDescription: mix.itemDescription,
      });
    });

    return dataToFilter;
  }

  render() {
    const { t, displayLocalOrders = false } = this.props;
    const { filter } = this.state;

    return (
      <div>
        <Row>
          <Col sm={6}>
            <h5>{displayLocalOrders ? t('Offline Orders') : t('Dispatch Tickets')} {this.data.length}</h5>
          </Col>
          <Col sm={6}>
            <FormControl
              type="text"
              value={filter}
              placeholder={t('Search')}
              onChange={this.handleChange}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <Filters filter={filter} data={this.data} renderData={(items) => (
              <DragDropContext onDragEnd={this.handleDragEnd}>
                <ReactTable
                  className=" schedule-table"
                  TrComponent={DragTrComponent}
                  TbodyComponent={DropTbodyComponent}
                  getTrProps={this.getTrProps}
                  data={items}
                  noDataText={' '}
                  columns={this.getColumns()}
                  showPaginationTop={false}
                  showPaginationBottom={false}
                  pageSizeOptions={[10, 20, 30]}
                  showPageSizeOptions={false}
                  resizable={false}
                  minRows={1}
                  defaultPageSize={100}
                />
              </DragDropContext>
            )} />
          </Col>
        </Row>
      </div>
    );
  }
}

export default withTranslation()(ScheduleTable);
